import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { sendDriverCodeAPI, sendReferralLinkAPI } from "../_request";
import { useAuth } from "context";
const InviteCustomerForm = ({ code, handleEmailPhoneSubmit }) => {
  const { showSnackbarWithMessage } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    personalEmail: "",
    workEmail: "",
    phoneNumber: "",
  });

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [showFirstForm, setShowFirstForm] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);
  const [errors, setErrors] = useState({ emailOrPhone: false });
  const [errorMessage, setErrorMessage] = useState("");

  const validateEmailOrPhone = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Simple email regex
    const phoneRegex = /^\d{10}$/; // Simple phone number regex (10 digits)
    if (!value) {
      return "This field is required.";
    }
    if (!emailRegex.test(value) && !phoneRegex.test(value)) {
      return "Please enter a valid email or phone number.";
    }
    return "";
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [formData]);

  const validateForm = () => {
    const newErrors = {
      firstName: !formData.firstName,
      personalEmail: !formData.personalEmail || !isEmailValid(formData.personalEmail),
      phoneNumber: !formData.phoneNumber,
      workEmail: formData.workEmail && !isEmailValid(formData.workEmail), // Optional but must be valid if filled
    };
    // Check if any of the required fields are empty
    return !Object.values(newErrors).includes(true);
  };
  const handleInviteSubmit = async () => {
    if (!isFormValid) {
      showSnackbarWithMessage({
        type: "error",
        message: "Please fill in all required fields.",
      });
      return;
    }
    try {
      const {
        data: { success, message },
      } = await sendDriverCodeAPI({ formData, code });
      if (success) {
        showSnackbarWithMessage({
          type: "success",
          message,
        });
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleReferralSubmit = async () => {
    try {
      const error = validateEmailOrPhone(emailOrPhone);
      if (!error) {
        const {
          data: { success, message },
        } = await sendReferralLinkAPI({ code, emailOrPhone });
        if (success) {
          showSnackbarWithMessage({
            type: "success",
            message,
          });
        } else {
          showSnackbarWithMessage({
            type: "error",
            message,
          });
        }
      }
    } catch (e) {
      console.error(e);
    }
  };
  const handleInputChange = (e) => {
    const value = e.target.value;
    setEmailOrPhone(value);

    // Validate on input change
    const error = validateEmailOrPhone(value);
    if (error) {
      setErrors({ emailOrPhone: true });
      setErrorMessage(error);
    } else {
      setErrors({ emailOrPhone: false });
      setErrorMessage(""); // Clear error message on valid input
    }
  };
  const handleToggle = () => {
    setShowFirstForm((prevState) => !prevState);
  };

  const isEmailValid = (email) => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailPattern.test(email);
  };

  return (
    <Grid container spacing={2}>
      <Grid container alignItems="center" justifyContent="center" spacing={2}>
        <Grid item>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#344767" }}>
            Send Customer Invite
          </Typography>
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Switch
                checked={!showFirstForm}
                onChange={() => setShowFirstForm((prevState) => !prevState)}
                color="success"
                sx={{
                  "& .MuiSwitch-track": {
                    backgroundColor: "green",
                  },
                }}
              />
            }
            label="Share Login URL"
          />
        </Grid>
      </Grid>
      {showFirstForm ? (
        <>
          <Grid item xs={12} md={6}>
            <MDInput
              label="First Name"
              name="firstName"
              variant="outlined"
              fullWidth
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MDInput
              label="Last Name"
              name="lastName"
              variant="outlined"
              fullWidth
              value={formData.lastName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MDInput
              label="Personal Email"
              name="personalEmail"
              variant="outlined"
              fullWidth
              value={formData.personalEmail}
              onChange={handleChange}
              error={!isEmailValid(formData.personalEmail)}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MDInput
              label="Work Email"
              name="workEmail"
              variant="outlined"
              fullWidth
              value={formData.workEmail}
              onChange={handleChange}
              error={formData.workEmail && !isEmailValid(formData.workEmail)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MDInput
              type="number"
              label="Phone Number"
              name="phoneNumber"
              variant="outlined"
              fullWidth
              value={formData.phoneNumber}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12} md={6}>
                <MDButton
                  variant="gradient"
                  color="success"
                  fullWidth
                  onClick={handleInviteSubmit}
                  disabled={!isEmailValid(formData.personalEmail) || !isFormValid}
                >
                  Send Invite
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} md={6}>
            <MDInput
              label="Email/Phone"
              name="emailOrPhone"
              variant="outlined"
              fullWidth
              value={emailOrPhone}
              onChange={handleInputChange}
              required
              error={errors.emailOrPhone} // Show error state
              helperText={errors.emailOrPhone ? errorMessage : ""}
              FormHelperTextProps={{
                sx: {
                  color: "red",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MDButton
              variant="gradient"
              color="success"
              fullWidth
              onClick={() => handleReferralSubmit()}
              disabled={errors.emailOrPhone}
            >
              Send
            </MDButton>
          </Grid>
        </>
      )}
    </Grid>
  );
};

InviteCustomerForm.propTypes = {
  handleEmailPhoneSubmit: PropTypes.func.isRequired,
  code: PropTypes.string,
};

export default InviteCustomerForm;
